import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const parseDateWithOffset = (dateString: string) => {
  return dayjs(dateString);
};

export const formatDateWithOffset = (date: any) => {
  return dayjs(date).format(); // Default ISO 8601 format with offset
};

export const getUtcDate = (date: any) => {
  return dayjs(date).utc().format();
};

export const getLocalDate = (date: any) => {
  return dayjs(date).local().format();
};

export const getSpecificTimeZoneDate = (date: any, timeZone: string) => {
  return dayjs(date).tz(timeZone).format();
};

export const serviceOrderChangeDateParser = (dateTimeRange: string) => {
  if (typeof dateTimeRange !== 'string') {
    return undefined;
  }
  // Regular expression to match the expected format
  const dateTimeRangeRegex = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - \d{2}:\d{2}$/;

  // Check if the input string matches the expected format
  if (!dateTimeRange.match(dateTimeRangeRegex)) {
    return undefined;
  }

  const [datePart, fromTime, _spliter, toTime] = dateTimeRange.split(' ');

  // Combine date part with from and to times to create full date-time strings
  const fromDateTimeStr = `${datePart} ${fromTime}`;
  const toDateTimeStr = `${datePart} ${toTime}`;

  // Parse the date and time parts as UTC
  const fromDateTime = dayjs.utc(fromDateTimeStr, 'DD/MM/YYYY HH:mm');
  const toDateTime = dayjs.utc(toDateTimeStr, 'DD/MM/YYYY HH:mm');

  // Convert to local timezone
  const fromDateTimeLocal = fromDateTime.local();
  const toDateTimeLocal = toDateTime.local();

  // Additional validation to ensure the parsed dates are valid
  if (!fromDateTimeLocal.isValid() || !toDateTimeLocal.isValid()) {
    return undefined;
  }

  // Combine date and time into the desired format
  const combinedString = `${fromDateTimeLocal.format('DD/MM/YYYY')} ${fromDateTimeLocal.format('HH:mm')} - ${toDateTimeLocal.format('HH:mm')}`;

  return combinedString;
};

export const getDateRangeFromPreset = (range?: string): { startAt?: string; endAt?: string } => {
  if (!range) return {};
  const now = dayjs();
  const fmt = (d: dayjs.Dayjs) => d.toISOString();
  switch (range) {
    case 'today':
      return { startAt: fmt(now.startOf('day')), endAt: fmt(now.endOf('day')) };
    case 'tomorrow': {
      const tomorrow = now.add(1, 'day');
      return { startAt: fmt(tomorrow.startOf('day')), endAt: fmt(tomorrow.endOf('day')) };
    }
    case 'this_week':
      return { startAt: fmt(now.startOf('week')), endAt: fmt(now.endOf('week')) };
    case 'this_month':
      return { startAt: fmt(now.startOf('month')), endAt: fmt(now.endOf('month')) };
    case 'last_month':
      return { startAt: fmt(now.subtract(1, 'month').startOf('month')), endAt: fmt(now.subtract(1, 'month').endOf('month')) };
    default:
      return {};
  }
};

/**
 * "Booked for the day, time still to be confirmed." Kept as a convention rather than a column:
 * a job whose start is exactly 00:00 or 01:00 has a date but no time - no garage books work in the
 * small hours, so the value is free to mean this. 01:00 as well as midnight because the same
 * midnight reads one hour later in summer time: a day booked with no time is stored as midnight,
 * and shown in the garage's own clock it is 01:00 for half the year. Every place that shows or
 * reasons about a job's time must go through here, or it will print "01:00" and treat the job as a
 * slot in the night. (The API's PendingSchedulingQuery and OrderManager.FormatSlot say the same.)
 */
export const TBC_TIME = '00:00';

/**
 * The earliest time of day a real booking can be given. Nobody works in the small hours, and
 * 00:00 / 01:00 are how a job with no time yet is stored (isTimeTbc) - so the time pickers start
 * here, and a real time can never be mistaken for "TBC".
 */
export const EARLIEST_BOOKING_MINUTES = 3 * 60;
export const isTimeTbc = (start?: Date | string | null) =>
  !!start && dayjs(start).hour() <= 1 && dayjs(start).minute() === 0;

/**
 * What a job is still missing before it can sit on the calendar: a day, a time (see isTimeTbc) and
 * somebody to do it - a technician or a workstation, either will do. A job missing any of them is
 * not drawn; it waits on the side panel's Unassigned list instead (and the API's
 * PendingSchedulingQuery says the same in SQL - keep the two in step).
 */
export const offCalendarGaps = (job: {
  startAt?: Date | string | null;
  userId?: string | number | null;
  workStationId?: string | null;
}) => ({
  date: !job.startAt,
  time: !!job.startAt && isTimeTbc(job.startAt),
  resource: !job.userId && !job.workStationId,
});

export const isOffCalendar = (job: Parameters<typeof offCalendarGaps>[0]) => {
  const gaps = offCalendarGaps(job);
  return gaps.date || gaps.time || gaps.resource;
};

export const roundUpToNext15 =(date: dayjs.Dayjs) => {
  const remainder = 15 - (date.minute() % 15);

  return remainder === 15 ? date.second(0) : date.add(remainder, 'minute').second(0);
};

export interface TimeOption {
  value: string;
  label: string;
}

export const MINUTES_IN_DAY = 24 * 60;

/** '09:30' -> 570 */
export const timeToMinutes = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

/** 95 -> '1h 35m', 40 -> '40m' */
export const formatLoggedTime = (totalMinutes: number) => {
  const total = Math.max(0, Math.floor(totalMinutes));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/** 570 -> '09:30' */
export const minutesToTime = (minutes: number) =>
  `${Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;

/** Select options for every `stepMinutes` between `fromMinutes` (inclusive) and `toMinutes` (exclusive). */
export const buildTimeOptions = (
  stepMinutes = 15,
  fromMinutes = 0,
  toMinutes = MINUTES_IN_DAY
): TimeOption[] =>
  Array.from({ length: Math.ceil((toMinutes - fromMinutes) / stepMinutes) }, (_, index) =>
    minutesToTime(fromMinutes + index * stepMinutes)
  ).map((time) => ({ value: time, label: time }));

/** Sorts, de-duplicates and formats a list of minute offsets as select options. */
export const toTimeOptions = (minutes: number[]): TimeOption[] =>
  [...new Set(minutes)]
    .sort((a, b) => a - b)
    .map((value) => ({ value: minutesToTime(value), label: minutesToTime(value) }));

/**
 * Minutes of a technician's work-log entry: the API's figure, or - for an entry still open (no
 * end yet) - the time elapsed since it started.
 */
export const workLogMinutes = (
  log: { minutes?: number | null; workedFrom: string | Date },
  now: dayjs.Dayjs = dayjs()
) => log.minutes ?? Math.max(0, now.diff(dayjs(log.workedFrom), 'minute'));

declare const abp: any;

const DEFAULT_CALENDAR_START = '07:00';

/**
 * The first time of day the calendar and its time pickers offer, as 'HH:mm'. A tenant setting
 * (App.Calendar.StartTime, changed in the database); anything unusable falls back to 07:00.
 */
export const getCalendarStartTime = (): string => {
  const match = /^(\d{1,2}):([0-5]\d)$/.exec(String(abp?.setting?.get?.('App.Calendar.StartTime') ?? '').trim());
  // Capped at midday so the grid always has room for a working day before it ends.
  if (!match || Number(match[1]) > 12) return DEFAULT_CALENDAR_START;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

export const getCalendarStartMinutes = (): number => {
  const [h, m] = getCalendarStartTime().split(':').map(Number);
  return h * 60 + m;
};

export const getCalendarStartHour = (): number => Math.floor(getCalendarStartMinutes() / 60);
